import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthToken } from '../../utils/cookieUtils';

const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

interface Facility {
  id: number;
  name: string;
}

interface DashboardDataParams {
  selectedFacility: Facility | null;
  enabledWidgetIds: string[];
  commsSinceHours: number;
  bolSinceHours: number;
}

interface DashboardDataResult {
  data: Record<string, any> | null;
  loading: boolean;
  isRefreshing: boolean;
  error: string | null;
  refresh: () => void;
}

// Each widget gets its own query + variable builder + data-key mapper

interface WidgetQuery {
  query: string;
  variables: (facilityId: string, params: { commsSinceHours: number; bolSinceHours: number }) => Record<string, any>;
  /** Maps the raw response data into the keys the widgets expect on the merged object */
  extractKeys: (responseData: any) => Record<string, any>;
}

const WIDGET_QUERIES: Record<string, WidgetQuery> = {
  'driver-mgmt': {
    query: `
      query DriverAssignments($facilityId: ID!) {
        driverAssignments: unassignedLiveDriverAssignments(facilityId: $facilityId, sinceHours: 48) {
          success
          message
          totalCount
          driverAssignments {
            id
            shipmentId
            status
            checkIn
            createdAt
            updatedAt
            driver { id firstName lastName }
            shipment {
              id
              referenceNumber
              direction
              metadata
              trailer { trailer_number carrier { name } }
            }
          }
        }
      }
    `,
    variables: (facilityId) => ({ facilityId }),
    extractKeys: (data) => ({ driverAssignments: data.driverAssignments }),
  },

  'dock-mgmt': {
    query: `
      query WarehouseAndFacility($facilityId: ID!) {
        warehouse(facilityId: $facilityId) {
          facilityId
          warehouseZones {
            zoneUuid
            zoneName
            configuration
            docks {
              dockUuid
              dockName
              dockStatus
              order_number
              trailer_name
              trailer_uuid
              carrier_name
              configuration
              trailer {
                active_lineage { last_moved_at uuid notes }
                status { uuid name }
              }
              shipment {
                id
                referenceNumber
                direction
                driver_assignment { created_at }
                metadata
              }
              driver { id firstName lastName }
            }
          }
        }
        facility(id: $facilityId) {
          id
          configuration
        }
      }
    `,
    variables: (facilityId) => ({ facilityId }),
    extractKeys: (data) => ({ warehouse: data.warehouse, facility: data.facility }),
  },

  'tasks': {
    query: `
      query TasksByFacility($facilityId: ID!) {
        tasksByFacility: tasks_by_facility(facility_id: $facilityId) {
          tasks {
            uuid
            data
            due_at
            completed_at
            user_id
            facility_id
            driver_id
            completed_by_user_id
            completed_by_driver_id
            modified_by_user_id
            metadata
            created_at
            updated_at
            task_template { uuid name task_template_json account_id }
            task_type { uuid type class }
            task_status { uuid status }
            from_location
            to_location
            from_object {
              ... on DockObject { uuid name }
              ... on YardSpotObject { uuid name facilityZoneUuid }
              ... on ExternalLocationObject { name }
              ... on FacilityZoneObject { uuid name }
            }
            to_object {
              ... on YardSpotObject { uuid name }
              ... on DockObject { uuid name }
              ... on WarehouseZoneObject { uuid name }
              ... on ExternalLocationObject { name }
              ... on FacilityZoneObject { uuid name }
            }
            trailer { trailer_number carrier { name } }
            related_movement_task {
              uuid
              from_location
              to_location
              from_object {
                ... on DockObject { uuid name }
                ... on YardSpotObject { uuid name facilityZoneUuid }
                ... on ExternalLocationObject { name }
                ... on FacilityZoneObject { uuid name }
              }
              to_object {
                ... on YardSpotObject { uuid name }
                ... on DockObject { uuid name }
                ... on WarehouseZoneObject { uuid name }
                ... on ExternalLocationObject { name }
                ... on FacilityZoneObject { uuid name }
              }
            }
          }
        }
      }
    `,
    variables: (facilityId) => ({ facilityId }),
    extractKeys: (data) => ({ tasksByFacility: data.tasksByFacility }),
  },

  'driver-comms': {
    query: `
      query DriverComms($facilityId: String!, $sinceHours: Int!) {
        driverComms: unreadThreadsByFacilityId(facilityId: $facilityId, sinceHours: $sinceHours) {
          threads {
            uuid
            message
            createdAt
            fromParticipant {
              uuid
              driver { firstName lastName phoneNumber }
            }
            shipment {
              referenceNumber
              direction
              metadata
              trailer {
                trailerNumber
                carrier { name }
                current_location {
                  ... on YardSpotObject { __typename uuid name }
                  ... on FacilityZoneObject { __typename uuid name }
                  ... on DockObject { __typename uuid name }
                }
              }
            }
          }
        }
      }
    `,
    variables: (facilityId, params) => ({ facilityId, sinceHours: params.commsSinceHours }),
    extractKeys: (data) => ({ driverComms: data.driverComms }),
  },

  'empty-trailers-scac': {
    query: `
      query TrailersInFacility($facilityId: ID!) {
        trailersInFacility: trailers_in_facility(facility_id: $facilityId) {
          uuid
          trailer_number
          carrier { name }
          status { name }
        }
      }
    `,
    variables: (facilityId) => ({ facilityId }),
    extractKeys: (data) => ({ trailersInFacility: data.trailersInFacility }),
  },

  'bol-dashboard': {
    query: `
      query BolDashboard($facilityId: String!, $sinceHours: Int) {
        bolDashboard: bolDashboardWidget(facilityId: $facilityId, sinceHours: $sinceHours) {
          items {
            shipmentId
            referenceNumber
            shipmentStatus
            direction
            bolId
            docNumber
            estimatedBolDate
            carrierName
            driverId
            driverName
            driverPhoneNumber
            driverFrId
            shipFrom
            shipTo
            checkIn
            checkOut
            stopCount
            pgi
            uploadedBol
            signedArtifact
            shipmentLastUpdated
            minutesSinceUpdate
            isOverdue
          }
          cachedAt
          facilityId
          totalCount
          overdueCount
        }
      }
    `,
    variables: (facilityId, params) => ({ facilityId, sinceHours: params.bolSinceHours }),
    extractKeys: (data) => ({ bolDashboard: data.bolDashboard }),
  },
};

async function fetchWidgetData(
  widgetId: string,
  facilityId: string,
  params: { commsSinceHours: number; bolSinceHours: number },
  signal: AbortSignal
): Promise<Record<string, any>> {
  const widgetQuery = WIDGET_QUERIES[widgetId];
  if (!widgetQuery) return {};

  const authToken = getAuthToken();
  const variables = widgetQuery.variables(facilityId, params);

  const response = await fetch(GRAPHQL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ query: widgetQuery.query, variables }),
    signal,
  });

  const result = await response.json();

  if (result.errors) {
    console.error(`Widget ${widgetId} query error:`, result.errors[0].message);
    return {};
  }

  return widgetQuery.extractKeys(result.data);
}

export function useDashboardData({
  selectedFacility,
  enabledWidgetIds,
  commsSinceHours,
  bolSinceHours,
}: DashboardDataParams): DashboardDataResult {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasDataRef = useRef(false);
  const prevFacilityIdRef = useRef<number | null | undefined>(undefined);

  hasDataRef.current = data !== null;

  const enabledKey = enabledWidgetIds.join(',');

  const fetchData = useCallback(async (forceLoading = false) => {
    if (!selectedFacility) return;

    const dataWidgets = enabledWidgetIds.filter(id => WIDGET_QUERIES[id]);
    if (dataWidgets.length === 0) {
      setData({});
      setLoading(false);
      setIsRefreshing(false);
      return;
    }

    if (forceLoading || !hasDataRef.current) {
      setLoading(true);
      setIsRefreshing(false);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const facilityId = String(selectedFacility.id);
      const params = { commsSinceHours, bolSinceHours };

      // Fire all widget queries in parallel
      const results = await Promise.allSettled(
        dataWidgets.map(id => fetchWidgetData(id, facilityId, params, abortController.signal))
      );

      if (abortController.signal.aborted) return;

      // Merge all successful results into one object
      const merged: Record<string, any> = {};
      const errors: string[] = [];

      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          Object.assign(merged, result.value);
        } else {
          if (result.reason?.name !== 'AbortError') {
            console.error(`Widget ${dataWidgets[i]} failed:`, result.reason);
            errors.push(`${dataWidgets[i]}: ${result.reason?.message || 'failed'}`);
          }
        }
      });

      setData(merged);

      if (errors.length > 0 && Object.keys(merged).length === 0) {
        setError(errors.join('; '));
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('Dashboard data fetch failed:', err);
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility?.id, enabledKey, commsSinceHours, bolSinceHours]);

  useEffect(() => {
    const facilityChanged = prevFacilityIdRef.current !== undefined &&
      prevFacilityIdRef.current !== selectedFacility?.id;
    prevFacilityIdRef.current = selectedFacility?.id ?? null;

    if (facilityChanged) {
      setData(null);
      hasDataRef.current = false;
    }

    fetchData(facilityChanged);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility?.id, enabledKey, commsSinceHours, bolSinceHours]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const refresh = useCallback(() => {
    fetchData(false);
  }, [fetchData]);

  return { data, loading, isRefreshing, error, refresh };
}
